import React from 'react';
import { Envelope, Napkin, Stationery, WelcomeSign } from '@withjoy/joykit/icons';
import { BasicDigitalIcon, ClassicPaperIcon, MagnetIcon, PostcardIcon, PremiumDigitalIcon } from './productPickerIcons';
import type { StationeryTemplateCategoryEnum } from '@graphql/generated-base';
import { isPostcard } from '@apps/card/utils/cardSizeName';
import type { ProductIconKind } from './ProductPicker.types';

type SizedOn = Readonly<{ category: StationeryTemplateCategoryEnum | undefined; widthInches: number; heightInches: number }>;

/**
 * Which glyph a picker row gets. Paper and postcard share a product type
 * (`stationeryCard`) and differ only by the size they sit on, so sized callers
 * pass the template's category and dimensions; digital rows have no size axis.
 */
export const getProductIconKind = (product: Readonly<{ productType: string }>, sizedOn?: SizedOn): ProductIconKind => {
  switch (product.productType) {
    case 'stationeryCard':
      return sizedOn && isPostcard(sizedOn.category, sizedOn.widthInches, sizedOn.heightInches) ? 'postcard' : 'paper';
    case 'magnet':
      return 'magnet';
    case 'digitalCard':
      return 'premiumDigital';
    case 'digital':
    case 'basicDigitalV2':
      return 'basicDigital';
    case 'napkin':
      return 'napkin';
    case 'sign':
      return 'sign';
    case 'envelope':
      return 'envelope';
    default:
      return 'other';
  }
};

// The five card products carry design's own glyphs (`productPickerIcons`);
// napkins, signs and anything new fall back to the nearest joykit icon until
// design draws one for them too.
//
// `envelope` and `paper` are the one pair that reads oddly together: the classic
// 5x7 card's own glyph IS an envelope (with a heart seal), so the two rows would
// both show an envelope if they ever appeared in the same picker. They cannot
// today — the envelope product is the only product on its template, and no card
// template links it — so this is raised for design rather than resolved by
// handing the envelope row a card glyph.
const ICONS: Record<ProductIconKind, React.ComponentType<React.ComponentProps<typeof Napkin>>> = {
  paper: ClassicPaperIcon,
  postcard: PostcardIcon,
  magnet: MagnetIcon,
  premiumDigital: PremiumDigitalIcon,
  basicDigital: BasicDigitalIcon,
  napkin: Napkin,
  sign: WelcomeSign,
  envelope: Envelope,
  other: Stationery,
};

// Decorative: the row's name is the label, so the glyph's own title ("Envelope") must not
// read ahead of it. An empty title drops the svg <title>; aria-hidden keeps the glyph out of
// the accessibility tree entirely.
export const ProductIcon = ({ kind }: { kind: ProductIconKind }) => {
  const Icon = ICONS[kind];
  return (
    <Icon
      size="md"
      title=""
      aria-hidden
    />
  );
};
